import { useCallback } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  addInternalConversationMembers,
  createInternalConversation,
  getInternalConversation,
  getInternalUnreadCount,
  internalCommunicationKeys,
  listInternalAttachments,
  listInternalConversations,
  listInternalMessages,
  listInternalRecipients,
  markInternalConversationRead,
  removeInternalConversationMember,
  renameInternalConversation,
  type InternalConversation,
} from '@/lib/internal-communications';
import { isConferenceConfigured } from '@/lib/conference/api';
import { useStartConference } from '@/lib/conference/hooks';
import { useAuthPermissions } from '@/stores/auth.store';
import { useConferenceStore } from '@/stores/conference.store';

export const TEAM_CHAT_PATH = '/dashboard/team-chat';

export type TeamChatScope = 'mine' | 'all';

// New messages also arrive over the notification stream, which invalidates
// every internal-communications query. Polling is only the fallback for a
// dropped stream, so it can stay relaxed.
const THREAD_POLL_MS = 5_000;
const LIST_POLL_MS = 15_000;
const UNREAD_POLL_MS = 30_000;

export function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message.trim() ? error.message : fallback;
}

export function useTeamChatPermissions() {
  const permissions = useAuthPermissions();
  return {
    canView: permissions.includes('internal_messages:view'),
    canCreate: permissions.includes('internal_messages:create'),
    canManage: permissions.includes('internal_messages:manage'),
    // Starting a call dials real SIP legs, so it follows the conference gate.
    canCall: isConferenceConfigured && permissions.includes('messages:edit'),
  };
}

export function useTeamChatUnreadCount() {
  const { canView } = useTeamChatPermissions();
  const unread = useQuery({
    queryKey: internalCommunicationKeys.unread(),
    // Counts job chats too: they are still reachable from team chat under the
    // Jobs filter, so the badge always points at something the user can open.
    queryFn: () => getInternalUnreadCount(),
    enabled: canView,
    refetchInterval: UNREAD_POLL_MS,
  });
  return canView ? (unread.data?.count ?? 0) : 0;
}

export function useTeamConversations(
  scope: TeamChatScope,
  search: string,
  contextType: 'GENERAL' | 'JOB' = 'GENERAL',
) {
  const { canView } = useTeamChatPermissions();
  return useQuery({
    queryKey: [...internalCommunicationKeys.conversations(), scope, search, contextType],
    // Job chats surface only under the Jobs filter, which asks for JOB; every
    // other filter stays on GENERAL. Splitting the fetch rather than hiding
    // rows in the browser also keeps job chats from consuming the page limit
    // and pushing real team chats out of the list.
    queryFn: () => listInternalConversations(scope, { search, limit: 100, contextType }),
    enabled: canView,
    refetchInterval: LIST_POLL_MS,
    placeholderData: (previous) => previous,
  });
}

export function useTeamConversation(conversationId: string | undefined) {
  return useQuery({
    queryKey: internalCommunicationKeys.conversation(conversationId ?? ''),
    queryFn: () => getInternalConversation(conversationId!),
    enabled: Boolean(conversationId),
    refetchInterval: LIST_POLL_MS,
  });
}

export function useTeamMessages(conversationId: string) {
  return useInfiniteQuery({
    queryKey: internalCommunicationKeys.messages(conversationId),
    queryFn: ({ pageParam }) =>
      listInternalMessages(conversationId, {
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: 100,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    refetchInterval: THREAD_POLL_MS,
  });
}

export function useTeamFiles(conversationId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: internalCommunicationKeys.attachments(conversationId),
    queryFn: ({ pageParam }) =>
      listInternalAttachments(conversationId, {
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: 50,
      }),
    enabled,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });
}

export function useTeamRecipients(search: string, enabled: boolean) {
  return useQuery({
    queryKey: internalCommunicationKeys.recipients(search),
    queryFn: () => listInternalRecipients({ search, limit: 100 }),
    enabled,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
}

function refreshConversationLists(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: internalCommunicationKeys.conversations() });
  void queryClient.invalidateQueries({ queryKey: internalCommunicationKeys.unread() });
}

export function useMarkConversationRead() {
  const queryClient = useQueryClient();
  return useCallback(
    (conversationId: string) => {
      void markInternalConversationRead(conversationId)
        .then(() => refreshConversationLists(queryClient))
        // A manager reading someone else's chat has no member row to clear.
        .catch(() => undefined);
    },
    [queryClient],
  );
}

function storeConversation(queryClient: QueryClient, conversation: InternalConversation) {
  queryClient.setQueryData(internalCommunicationKeys.conversation(conversation.id), conversation);
  refreshConversationLists(queryClient);
}

export function useCreateTeamConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createInternalConversation,
    onSuccess: (conversation) => storeConversation(queryClient, conversation),
    onError: (error) => toast.error(errorMessage(error, 'Could not start the chat')),
  });
}

export function useRenameTeamConversation(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (title: string) => renameInternalConversation(conversationId, title),
    onSuccess: (conversation) => storeConversation(queryClient, conversation),
    onError: (error) => toast.error(errorMessage(error, 'Could not rename the group')),
  });
}

export function useAddTeamMembers(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) => addInternalConversationMembers(conversationId, userIds),
    onSuccess: (conversation) => storeConversation(queryClient, conversation),
    onError: (error) => toast.error(errorMessage(error, 'Could not add those people')),
  });
}

export function useRemoveTeamMember(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => removeInternalConversationMember(conversationId, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: internalCommunicationKeys.conversation(conversationId),
      });
      refreshConversationLists(queryClient);
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not remove that person')),
  });
}

/**
 * Calls teammates from a chat. An internal call is a conference with the chat's
 * other members, started the same way the mobile app starts one: the service
 * prepares a room, this tab dials into it, and everyone else is rung with our
 * name once our leg arrives. The conference overlay shows the live call.
 */
export function useTeamCall() {
  const start = useStartConference();

  const call = useCallback(
    async (conversation: InternalConversation, userId: string | null) => {
      const conference = useConferenceStore.getState();
      if (conference.conferenceId) {
        // Already on a team call: bring it forward rather than dialing again.
        conference.restore();
        return;
      }
      const targets = conversation.members.filter((member) => member.user.id !== userId);
      if (targets.length === 0) {
        toast.error('No one else is in this chat.');
        return;
      }
      const hostName = conversation.members.find((member) => member.user.id === userId)?.user
        .displayName;
      try {
        const result = await start.mutateAsync({
          targetUserIds: targets.map((member) => member.user.id),
          ...(hostName ? { hostName } : {}),
        });
        useConferenceStore.getState().setConferenceId(result.conferenceId);
        useConferenceStore.getState().open();
      } catch {
        // useStartConference already told the user why.
      }
    },
    [start],
  );

  return { call, isStarting: start.isPending };
}
